// assets/src/js/hero.js
//
// Vanilla — no React, no animation library. The hero is server-rendered
// (includes/hero.php) so it paints on first response; this builds the
// rotating word as an SVG and swaps classes. Every duration, easing and
// stagger lives in hero.css.
//
// The word draws on for real: each letter is the actual Permanent Marker
// glyph, revealed by a thick stroke travelling along that letter's
// centreline — the path a pen would take. Animating the glyph's *outline*
// instead would trace its edge, which reads as tracing, not writing.
// Centrelines come from tools/gen-marker-paths.py.
//
// This used to bundle GSAP + SplitText: 129KB gzipped, because `gsap/all`
// drags in every plugin. CSS transitions do the whole thing.

import MARKER from "./marker-paths.js";

const CYCLE_MS = 5700; // hold + transition — matches the time bar's fill
const SVG_NS = "http://www.w3.org/2000/svg";

let maskSeq = 0;

/** Advance width for a character the generator didn't cover. */
function fallbackWidth(ch) {
	const canvas = (fallbackWidth.canvas ||= document.createElement("canvas"));
	const ctx = canvas.getContext("2d");
	ctx.font = `${MARKER.units}px "Permanent Marker", sans-serif`;
	return ctx.measureText(ch).width || MARKER.space;
}

function el(name, attrs) {
	const node = document.createElementNS(SVG_NS, name);
	for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
	return node;
}

/**
 * Build the word as one SVG: a <text> per glyph, positioned by the same pen
 * x as its centreline path, so the mask can never drift out of register with
 * the letter it reveals. Laying the word out with our own advance widths
 * rather than the browser's is the point — the two only have to agree with
 * themselves.
 */
function buildWord(text) {
	const { units, top, bottom, glyphs, stroke } = MARKER;
	const chars = [...text.toUpperCase()];

	const svg = el("svg", { "aria-hidden": "true", focusable: "false", class: "word-svg" });
	const maskId = `rdp-ink-${++maskSeq}`;
	const ink = el("g", {
		class: "ink",
		fill: "none",
		stroke: "#fff",
		"stroke-width": stroke,
		"stroke-linecap": "round",
		"stroke-linejoin": "round",
	});
	const masked = el("g", { mask: `url(#${maskId})` });

	let pen = 0;
	let index = 0;
	for (const ch of chars) {
		const glyph = glyphs[ch];
		const width = glyph ? glyph.w : fallbackWidth(ch);
		if (ch !== " ") {
			const t = el("text", { x: pen, y: 0 });
			t.textContent = ch;
			masked.append(t);

			const path = glyph
				? el("path", { d: glyph.d, transform: `translate(${pen} 0)`, pathLength: "1" })
				// No centreline for this character (an accent, a lowercase letter,
				// anything the generator skipped). Sweep a bar the height of the
				// ink box across it instead: not a pen stroke, but it draws on
				// and erases on the same timing as its neighbours, where leaving
				// it unmasked would pop it in fully formed and never clear it.
				: el("path", {
						d: `M${pen} ${(top + bottom) / 2}h${width}`,
						pathLength: "1",
						"stroke-width": bottom - top,
						"stroke-linecap": "butt",
					});
			path.style.setProperty("--i", index++);
			ink.append(path);
		}
		pen += width;
	}

	// A generous user-space mask region: the default bounding box would clip
	// a stroke this thick.
	const pad = stroke;
	const mask = el("mask", {
		id: maskId,
		maskUnits: "userSpaceOnUse",
		x: -pad,
		y: top - pad,
		width: pen + 2 * pad,
		height: bottom - top + 2 * pad,
	});
	mask.append(ink);

	const defs = el("defs", {});
	defs.append(mask);
	svg.append(defs, masked);
	svg.setAttribute("viewBox", `0 ${top} ${pen} ${bottom - top}`);
	svg.style.width = `${pen / units}em`;
	svg.style.height = `${(bottom - top) / units}em`;
	return svg;
}

function markSegments(segments, activeIndex, durationMs) {
	segments.forEach((seg, i) => {
		seg.classList.remove("is-active", "is-done");
		if (i < activeIndex) seg.classList.add("is-done");
		if (i === activeIndex) {
			seg.classList.add("is-active");
			seg.style.setProperty("--rdp-timebar-dur", `${durationMs}ms`);
		}
	});
}

/**
 * Only the first image is in the HTML; the rest would otherwise be fetched
 * at the moment they're wiped in, which shows as a blank panel on a slow
 * connection. Warm them once the page is idle instead.
 */
function prefetch(items) {
	const run = () => items.slice(1).forEach((item) => { new Image().src = item.image; });
	if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 3000 });
	else window.addEventListener("load", run, { once: true });
}

function init() {
	const items = window.rdpHeroData?.items || [];
	const textSlots = document.querySelectorAll("#design-item .slot");
	const imageSlots = document.querySelectorAll("#hero-images .slot");
	const segments = document.querySelectorAll(".timebar-segment");

	if (items.length < 2 || textSlots.length < 2 || imageSlots.length < 2) return;

	prefetch(items);

	// The server-rendered word is plain text — the fallback if this script
	// never runs. Replace it with the drawable SVG version.
	textSlots[0].replaceChildren(buildWord(items[0].name));

	// index 0 is already on screen (server-rendered); first transition goes to index 1.
	let index = 1 % items.length;
	let active = 0;

	function playNext() {
		markSegments(segments, (index - 1 + items.length) % items.length, CYCLE_MS);

		const next = items[index];
		const outText = textSlots[active];
		const inText = textSlots[1 - active];
		const outImg = imageSlots[active];
		const inImg = imageSlots[1 - active];

		// Clear whatever state this slot was left in two cycles ago.
		inText.classList.remove("is-exit");
		inText.replaceChildren(buildWord(next.name));
		inText.classList.add("is-enter", "is-arriving");

		inImg.src = next.image;
		inImg.classList.remove("is-under");
		inImg.classList.add("is-wipe", "is-over");
		outImg.classList.remove("is-over");
		outImg.classList.add("is-under");

		// Commit the undrawn state, then release it so the transitions run.
		void inText.offsetWidth;
		inText.classList.remove("is-enter");
		inImg.classList.remove("is-wipe");
		// is-arriving is the write-on's delay; the erase brings its own.
		outText.classList.remove("is-arriving");
		outText.classList.add("is-exit");

		active = 1 - active;
		index = (index + 1) % items.length;
	}

	setInterval(playNext, CYCLE_MS);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
